import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket, Lightning, Bank, GraduationCap, Wrench, ChartLineUp,
  Handshake, Cross, ArrowRight, Check, ArrowSquareOut,
  BookOpen, Target, CheckCircle, Sparkle, Brain, Play, 
  CurrencyDollar, CaretRight, CaretLeft
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourceHubPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [usedResources, setUsedResources] = useState(() => {
    const saved = localStorage.getItem('powr-used-resources');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm] = useState({
    goal: '',
    experience_level: '',
    time_available: ''
  });
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [aiPlan, setAiPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await axios.get(`${API}/ai-plan/me`, {
          headers: getAuthHeader()
        });
        if (response.data) {
          setAiPlan(response.data);
        }
      } catch (error) {
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchPlan();
  }, [getAuthHeader]);

  useEffect(() => {
    localStorage.setItem('powr-used-resources', JSON.stringify(usedResources));
  }, [usedResources]);

  const toggleUsed = (resourceId) => {
    setUsedResources(prev => 
      prev.includes(resourceId) 
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleGeneratePlan = async (e) => {
    e.preventDefault();
    if (!planForm.goal || !planForm.experience_level || !planForm.time_available) {
      toast.error('Please answer all questions');
      return;
    }

    setGeneratingPlan(true);
    try {
      const response = await axios.post(`${API}/ai-plan/generate`, planForm, {
        headers: getAuthHeader()
      });
      setAiPlan(response.data);
      setShowPlanForm(false);
      toast.success('Your personalized plan is ready!');
    } catch (error) {
      toast.error('Failed to generate plan. Please try again.');
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleResetPlan = async () => {
    try {
      await axios.delete(`${API}/ai-plan/me`, {
        headers: getAuthHeader()
      });
      setAiPlan(null);
      setPlanForm({ goal: '', experience_level: '', time_available: '' });
      toast.success('Plan reset. Create a new one!');
    } catch (error) {
      toast.error('Failed to reset plan');
    }
  };

  const handleStartBlueprint = () => {
    const path = aiPlan?.recommended_path || 'affiliate_marketing';
    navigate(`/blueprint?path=${path}`);
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const resources = {
    funding: [
      { id: 'hello-alice', name: 'Hello Alice', description: 'Free grants, funding opportunities, and resources specifically for underrepresented entrepreneurs.', url: 'https://helloalice.com', tag: 'Grants' },
      { id: 'ifundwomen', name: 'IFundWomen', description: 'Grants and coaching for entrepreneurs. Multiple funding opportunities throughout the year.', url: 'https://ifundwomen.com', tag: 'Grants' },
      { id: 'sba', name: 'SBA (Small Business Administration)', description: 'Government-backed loans, grants, and business counseling. The foundation for serious funding.', url: 'https://sba.gov', tag: 'Loans & Grants' }
    ],
    learn: [
      { id: 'score', name: 'SCORE', description: 'Free business mentoring and education. Connect with experienced mentors who guide you step by step.', url: 'https://score.org', tag: 'Mentorship' },
      { id: 'hubspot-academy', name: 'HubSpot Academy', description: 'Free certifications in marketing, sales, and business. Industry-recognized credentials.', url: 'https://academy.hubspot.com', tag: 'Certifications' },
      { id: 'google-digital-garage', name: 'Google Digital Garage', description: 'Free courses on digital marketing, data, and career development from Google.', url: 'https://learndigital.withgoogle.com/digitalgarage', tag: 'Digital Skills' }
    ],
    build: [
      { id: 'chatgpt', name: 'ChatGPT', description: 'AI assistant for content creation, business planning, customer service scripts, and more.', url: 'https://chat.openai.com', tag: 'AI' },
      { id: 'canva', name: 'Canva', description: 'Design tool for social media graphics, presentations, logos, and marketing materials.', url: 'https://canva.com', tag: 'Design' },
      { id: 'mailchimp', name: 'Mailchimp', description: 'Email marketing platform with a generous free tier. Build and nurture your audience.', url: 'https://mailchimp.com', tag: 'Email' },
      { id: 'carrd', name: 'Carrd', description: 'Simple, free one-page websites. Perfect for landing pages and link-in-bio sites.', url: 'https://carrd.co', tag: 'Websites' }
    ],
    traffic: [
      { id: 'google-trends', name: 'Google Trends', description: 'See what people are searching for. Find trending topics and validate your ideas.', url: 'https://trends.google.com', tag: 'Research' },
      { id: 'answerthepublic', name: 'AnswerThePublic', description: 'Discover what questions people are asking. Create content that answers real needs.', url: 'https://answerthepublic.com', tag: 'Content Ideas' }
    ],
    network: [
      { id: 'black-founders', name: 'Black Founders', description: 'Community and resources for Black entrepreneurs in tech and startups.', url: 'https://blackfounders.com', tag: 'Community' },
      { id: 'nbmbaa', name: 'National Black MBA Association', description: 'Professional network, career opportunities, and business resources.', url: 'https://nbmbaa.org', tag: 'Professional' },
      { id: 'local-chamber', name: 'Local Chamber of Commerce', description: 'Search "[Your City] Chamber of Commerce" — networking events, business resources, and local connections.', url: 'https://www.uschamber.com/co/chambers', tag: 'Local' }
    ],
    foundation: [
      { id: 'bible-proverbs', name: 'The Bible — Proverbs', description: 'Wisdom for business, money, and life. Start with Proverbs for practical daily guidance.', url: 'https://www.bible.com/bible/111/PRO.1.NIV', tag: 'Wisdom' },
      { id: 'bible-discipline', name: 'The Bible — Discipline & Work', description: 'Colossians 3:23, Proverbs 10:4, Ecclesiastes 9:10 — foundations for work ethic and purpose.', url: 'https://www.bible.com/bible/111/COL.3.23.NIV', tag: 'Work Ethic' }
    ]
  };

  const ResourceCard = ({ resource, onToggleUsed, isUsed }) => (
    <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="h-full">
      <div className={`card-premium p-6 h-full flex flex-col relative ${isUsed ? 'border-[#C4A661]/30' : ''}`}>
        {isUsed && <div className="absolute top-4 right-4"><CheckCircle size={20} className="text-[#C4A661]" weight="fill" /></div>}
        <div className="flex items-start justify-between mb-3">
          <span className="text-overline">{resource.tag}</span>
        </div>
        <h3 className="text-lg font-bold mb-2 text-[#F4F0E6]">{resource.name}</h3>
        <p className="text-white/50 text-sm leading-relaxed flex-grow mb-5">{resource.description}</p>
        <div className="flex gap-2">
          <a href={resource.url} target="_blank" rel="noopener noreferrer" className="flex-1">
            <Button className="w-full btn-outline h-10 text-sm">
              Visit <ArrowSquareOut size={14} className="ml-2" />
            </Button>
          </a>
          <Button onClick={() => onToggleUsed(resource.id)} className={`h-10 px-4 ${isUsed ? 'bg-[#C4A661]/20 border border-[#C4A661]/30 text-[#C4A661]' : 'btn-outline'}`}>
            <Check size={16} weight={isUsed ? 'bold' : 'regular'} />
          </Button>
        </div>
      </div>
    </motion.div>
  );

  const SectionHeader = ({ icon: Icon, title, description, id }) => (
    <div id={id} className="mb-8 scroll-mt-24">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-[#C4A661]/10 border border-[#C4A661]/30 flex items-center justify-center">
          <Icon size={24} className="text-[#C4A661]" weight="duotone" />
        </div>
        <h2 className="text-2xl font-bold text-[#F4F0E6]">{title}</h2>
      </div>
      <p className="text-white/50">{description}</p>
    </div>
  );

  const ActionPrompt = ({ text }) => (
    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-8 p-5 bg-gradient-to-r from-[#C4A661]/10 to-transparent border-l-2 border-[#C4A661]">
      <p className="text-sm">
        <span className="text-[#C4A661] font-bold uppercase tracking-widest mr-3">Action:</span>
        <span className="text-white/60">{text}</span>
      </p>
    </motion.div>
  );

  const totalResources = Object.values(resources).flat().length;
  const usedCount = usedResources.length;
  const progressPercent = Math.round((usedCount / totalResources) * 100);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="nav-glass border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/community" className="text-2xl font-bold tracking-tight text-[#C4A661]">POWR</Link>
          <div className="flex items-center gap-6">
            <Link to="/blueprint" className="text-sm text-[#C4A661] hover:text-[#D4B671] transition-colors flex items-center gap-2">
              <CurrencyDollar size={16} /> First $100 Blueprint
            </Link>
            <Link to="/community" className="text-sm text-white/60 hover:text-[#F4F0E6] transition-colors flex items-center gap-2">
              <CaretLeft size={14} /> Back
            </Link>
          </div>
        </div>
      </header>

      {/* Scripture Banner */}
      <div className="bg-[#C4A661] py-4">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-[#0A0A0A] text-sm font-medium">
            <Cross size={14} className="inline-block mr-2 -mt-0.5" weight="bold" />
            "The plans of the diligent lead surely to abundance." — Proverbs 21:5
          </p>
        </div>
      </div>

      {/* Hero Section - Light */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#FDFDFD] border-b border-[#E5E5E5]">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="text-center">
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0A0A0A] border border-[#262626] flex items-center justify-center">
                <Rocket size={28} className="text-[#C4A661]" weight="duotone" />
              </div>
            </motion.div>
            
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold tracking-tighter mb-4 text-[#0A0A0A]">
              POWR <span className="text-[#C4A661]">Resource Hub</span>
            </motion.h1>
            
            <motion.p variants={fadeUp} className="text-lg text-[#525252] max-w-2xl mx-auto">
              Everything here is free—but it only works if you use it.
              <br /><span className="text-[#0A0A0A] font-medium font-editorial italic">Pick what you need. Apply it. Move forward.</span>
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* AI Plan Section */}
      <section className="py-12 px-6 md:px-12 lg:px-24 border-b border-white/10">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="bg-gradient-to-br from-[#C4A661]/10 via-[#121212] to-[#121212] border border-[#C4A661]/30 p-8 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#C4A661]/5 rounded-full blur-3xl -mr-32 -mt-32" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <Brain size={32} className="text-[#C4A661]" weight="duotone" />
                  <h2 className="text-2xl md:text-3xl font-bold text-[#F4F0E6]">Get Your Plan</h2>
                </div>
                
                <p className="text-white/50 mb-8 max-w-xl">
                  Answer a few questions and we'll show you exactly what to do next. 
                  <span className="font-editorial italic text-white/70"> No guessing. No overwhelm. Just a clear path forward.</span>
                </p>

                {loadingPlan ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-[#C4A661] border-t-transparent animate-spin"></div>
                  </div>
                ) : aiPlan ? (
                  <div className="space-y-6">
                    <div className="p-6 bg-[#050505] border border-white/10">
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkle size={20} className="text-[#C4A661]" weight="fill" />
                        <span className="text-overline">Your Recommended Path</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-[#F4F0E6]">{aiPlan.recommended_path_display}</h3>
                      
                      <div className="mb-6">
                        <p className="text-sm text-white/40 mb-2">Tools to use:</p>
                        <div className="flex flex-wrap gap-2">
                          {aiPlan.recommended_tools.map((tool, i) => (
                            <span key={i} className="px-3 py-1 bg-white/5 border border-white/10 text-sm text-white/70">{tool}</span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <p className="text-sm text-white/40 mb-3">Your action steps:</p>
                        <div className="space-y-3">
                          {aiPlan.action_steps.map((step, i) => (
                            <div key={i} className="flex gap-3 items-start">
                              <span className="w-6 h-6 bg-[#C4A661] text-[#050505] text-sm font-bold flex items-center justify-center flex-shrink-0">{step.step_number}</span>
                              <div>
                                <p className="font-semibold text-[#F4F0E6]">{step.title}</p>
                                <p className="text-sm text-white/50">{step.action}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button onClick={handleStartBlueprint} className="flex-1 btn-gold h-14 text-lg" data-testid="start-plan-btn">
                        <Play size={20} className="mr-2" weight="fill" />
                        Start My Plan
                      </Button>
                      <Button onClick={handleResetPlan} className="btn-outline h-14">
                        Get New Plan
                      </Button>
                    </div>
                  </div>
                ) : showPlanForm ? (
                  <form onSubmit={handleGeneratePlan} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold mb-3 text-[#F4F0E6]">What's your primary goal?</label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { value: 'make_money', label: 'Make Money', desc: 'Quick income' },
                          { value: 'start_business', label: 'Start a Business', desc: 'Build something' },
                          { value: 'learn_skills', label: 'Learn Skills', desc: 'Level up' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setPlanForm({ ...planForm, goal: opt.value })}
                            className={`p-4 border text-left transition-all ${planForm.goal === opt.value ? 'border-[#C4A661] bg-[#C4A661]/10' : 'border-white/10 hover:border-white/20'}`}
                          >
                            <p className="font-semibold text-[#F4F0E6]">{opt.label}</p>
                            <p className="text-sm text-white/40">{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-3 text-[#F4F0E6]">What's your experience level?</label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { value: 'beginner', label: 'Beginner', desc: 'Just starting out' },
                          { value: 'intermediate', label: 'Intermediate', desc: 'Some experience' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setPlanForm({ ...planForm, experience_level: opt.value })}
                            className={`p-4 border text-left transition-all ${planForm.experience_level === opt.value ? 'border-[#C4A661] bg-[#C4A661]/10' : 'border-white/10 hover:border-white/20'}`}
                          >
                            <p className="font-semibold text-[#F4F0E6]">{opt.label}</p>
                            <p className="text-sm text-white/40">{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-3 text-[#F4F0E6]">How much time do you have daily?</label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { value: '1-2_hours', label: '1-2 Hours', desc: 'Part-time effort' },
                          { value: '3-5_hours', label: '3-5 Hours', desc: 'Serious commitment' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setPlanForm({ ...planForm, time_available: opt.value })}
                            className={`p-4 border text-left transition-all ${planForm.time_available === opt.value ? 'border-[#C4A661] bg-[#C4A661]/10' : 'border-white/10 hover:border-white/20'}`}
                          >
                            <p className="font-semibold text-[#F4F0E6]">{opt.label}</p>
                            <p className="text-sm text-white/40">{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button type="submit" disabled={generatingPlan} className="flex-1 btn-gold h-12" data-testid="generate-plan-btn">
                        {generatingPlan ? (
                          <>
                            <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent animate-spin mr-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkle size={18} className="mr-2" weight="fill" />
                            Generate My Plan
                          </>
                        )}
                      </Button>
                      <Button type="button" onClick={() => setShowPlanForm(false)} className="btn-outline h-12">
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button onClick={() => setShowPlanForm(true)} className="btn-gold h-14 px-8 text-lg" data-testid="get-plan-btn">
                      <Sparkle size={20} className="mr-2" weight="fill" />
                      Get My Personalized Plan
                    </Button>
                    <Link to="/blueprint">
                      <Button className="btn-outline h-14 px-8">
                        <CurrencyDollar size={20} className="mr-2" />
                        Skip to First $100 Blueprint
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Start Section */}
      <section className="py-12 px-6 md:px-12 lg:px-24 border-b border-white/10">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="card-premium p-8">
              <div className="flex items-center gap-3 mb-6">
                <Lightning size={28} className="text-[#C4A661]" weight="fill" />
                <h2 className="text-2xl font-bold text-[#F4F0E6]">Quick Navigation</h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button onClick={() => scrollToSection('learn')} className="btn-outline h-12">
                  <GraduationCap size={18} className="mr-2" /> Learn Basics
                </Button>
                <Button onClick={() => scrollToSection('build')} className="btn-outline h-12">
                  <Wrench size={18} className="mr-2" /> Build Tools
                </Button>
                <Button onClick={() => scrollToSection('funding')} className="btn-outline h-12">
                  <Bank size={18} className="mr-2" /> Find Funding
                </Button>
                <Button onClick={() => scrollToSection('traffic')} className="btn-outline h-12">
                  <ChartLineUp size={18} className="mr-2" /> Get Traffic
                </Button>
              </div>

              {usedCount > 0 && (
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-white/50">Resources Used</span>
                    <span className="text-[#C4A661] font-mono">{usedCount}/{totalResources}</span>
                  </div>
                  <div className="h-1 bg-white/10 overflow-hidden">
                    <motion.div className="h-full bg-[#C4A661]" initial={{ width: 0 }} animate={{ width: `${progressPercent}%` }} transition={{ duration: 0.5 }} />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Resource Sections */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="container mx-auto max-w-5xl space-y-20">
          
          <div>
            <SectionHeader icon={Bank} title="Funding + Grants" description="Capital to start or grow your business." id="funding" />
            <div className="grid md:grid-cols-3 gap-6">
              {resources.funding.map(resource => (
                <ResourceCard key={resource.id} resource={resource} onToggleUsed={toggleUsed} isUsed={usedResources.includes(resource.id)} />
              ))}
            </div>
            <ActionPrompt text="Choose one funding source and complete their application this week." />
          </div>

          <div>
            <SectionHeader icon={GraduationCap} title="Learn (Free Education)" description="Knowledge is free. Execution is where value comes from." id="learn" />
            <div className="grid md:grid-cols-3 gap-6">
              {resources.learn.map(resource => (
                <ResourceCard key={resource.id} resource={resource} onToggleUsed={toggleUsed} isUsed={usedResources.includes(resource.id)} />
              ))}
            </div>
            <ActionPrompt text="Pick one course and commit to completing it in 30 days." />
          </div>

          <div>
            <SectionHeader icon={Wrench} title="Build (Tools)" description="The tools to create, design, and launch." id="build" />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {resources.build.map(resource => (
                <ResourceCard key={resource.id} resource={resource} onToggleUsed={toggleUsed} isUsed={usedResources.includes(resource.id)} />
              ))}
            </div>
            <ActionPrompt text="Sign up for one tool and create something with it today." />
          </div>

          <div>
            <SectionHeader icon={ChartLineUp} title="Traffic + Content Ideas" description="Understand what people want." id="traffic" />
            <div className="grid md:grid-cols-2 gap-6">
              {resources.traffic.map(resource => (
                <ResourceCard key={resource.id} resource={resource} onToggleUsed={toggleUsed} isUsed={usedResources.includes(resource.id)} />
              ))}
            </div>
            <ActionPrompt text="Research one topic and write down 10 content ideas." />
          </div>

          <div>
            <SectionHeader icon={Handshake} title="Network" description="Connect with people who understand your journey." id="network" />
            <div className="grid md:grid-cols-3 gap-6">
              {resources.network.map(resource => (
                <ResourceCard key={resource.id} resource={resource} onToggleUsed={toggleUsed} isUsed={usedResources.includes(resource.id)} />
              ))}
            </div>
            <ActionPrompt text="Join one community and introduce yourself this week." />
          </div>

          <div>
            <SectionHeader icon={Cross} title="Foundation" description="Everything we build should be grounded in something deeper." id="foundation" />
            <div className="grid md:grid-cols-2 gap-6">
              {resources.foundation.map(resource => (
                <ResourceCard key={resource.id} resource={resource} onToggleUsed={toggleUsed} isUsed={usedResources.includes(resource.id)} />
              ))}
            </div>
            <ActionPrompt text="Read one chapter of Proverbs and apply one piece of wisdom." />
          </div>

        </div>
      </section>

      {/* Final Execution Section */}
      <section className="py-20 px-6 md:px-12 lg:px-24 border-t border-white/10">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Target size={32} className="text-[#C4A661] mx-auto mb-6" weight="duotone" />
            <h2 className="text-3xl font-bold mb-10 text-[#F4F0E6]">The Execution System</h2>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                { num: 1, title: 'Get Your Plan', desc: 'Use the AI system above' },
                { num: 2, title: 'Start the Blueprint', desc: '7 days of guided action' },
                { num: 3, title: 'Make Your First $100', desc: 'Real results, real money' }
              ].map(step => (
                <div key={step.num} className="card-premium p-6">
                  <div className="w-12 h-12 bg-[#C4A661] text-[#050505] font-bold text-xl flex items-center justify-center mx-auto mb-4">{step.num}</div>
                  <h3 className="font-bold mb-2 text-[#F4F0E6]">{step.title}</h3>
                  <p className="text-white/40 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-[#121212] to-[#050505] border border-[#C4A661]/30 p-8 mb-10">
              <p className="text-xl text-white/60 mb-2">The difference isn't <span className="text-[#F4F0E6] font-semibold">access</span>.</p>
              <p className="text-2xl font-bold text-[#C4A661] font-editorial italic">The difference is execution.</p>
            </div>

            <Link to="/blueprint">
              <Button className="btn-gold h-14 px-10 text-lg" data-testid="start-blueprint-btn">
                <CurrencyDollar size={20} className="mr-2" />
                Start First $100 Blueprint
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-white/10">
        <div className="container mx-auto max-w-5xl text-center">
          <p className="text-sm text-white/40">POWR — Purpose. Ownership. Wealth. Responsibility.</p>
        </div>
      </footer>
    </div>
  );
};

export default ResourceHubPage;
